import { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Chip,
  Box,
  Breadcrumbs,
  Link,
  Avatar,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  ButtonGroup,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  KeyboardArrowDown,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { dataLocalStorage, getLocalStorage } from "src/helper/publicFunction";
import { Loader2 } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  updateManifestStatus,
  sendManifestToDriver,
  sendManifestToDispatchers,
  deleteManifests,
  updateManifestName,
} from "src/queries/query";
import EditableInput from "src/components/apps/teams-components/editableInput";
import { toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router";
import SendToDispatchersModal from "src/components/apps/ManifestsComponents/SendToDispatchersModal";
import SendToDriverModal from "src/components/apps/ManifestsComponents/SendToDriverModal";
import AddOrdersToInvoiceModal from "src/components/invoice/AddOrdersToInvoiceModal";

const TopBar = ({
  orderNumber,
  manifestName,
  status,
  isOrderOSND,
  isOSND = false,
  handleSubmit,
  handleMarkAsDelivered,
  isMarkingDelivered = false,
  isLoading,
  actionMenuItems,
  isPending,
  notSubmitButton = false,
  isManifest = false,
  manifestUuid = null,
  driver = null,
  carrierId = null,
  handleCreateInvoice = null,
  isCreatingInvoice = false,
  orderId = null,
}) => {
  const userInfo = getLocalStorage(dataLocalStorage.userinfo);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleSaveName = async (newName) => {
    try {
      await updateManifestName(manifestUuid, newName?.toUpperCase() || "");
      queryClient.invalidateQueries(["manifest", manifestUuid]);
      toast.success("Manifest name updated successfully");
    } catch (error) {
      toast.error("Failed to update manifest name");
      throw error;
    }
  };

  // Robust check for OSND context
  const effectiveIsOSND = isOSND || pathname?.toLowerCase()?.includes("/osnd/");

  const getStatusLabel = (currentStatus) => {
    // Don't transform status - show raw status from API
    return currentStatus || "New";
  };

  // Derive driverUuid from driver object if available
  const driverUuid =
    driver?.uuid ||
    driver?.driverUuid ||
    driver?.id ||
    driver?.driverId ||
    null;
  // Mutation for sending manifest to driver (backend will update status to ASN)
  const sendToDriverMutation = useMutation({
    mutationFn: ({ manifestUuid, driverUuid }) =>
      sendManifestToDriver(manifestUuid, driverUuid),
    onSuccess: () => {
      toast.success("Manifest sent to driver");
      queryClient.invalidateQueries(["manifest", manifestUuid]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send manifest to driver");
    },
  });

  const isDisabled =
    sendToDriverMutation.isPending ||
    status === "COM" ||
    status === "Completed" ||
    status === "MOV" ||
    status === "Moving" ||
    (!driverUuid && !carrierId);

  // Mutation for updating manifest status (used for Complete Manifest)
  const updateStatusMutation = useMutation({
    mutationFn: ({ uuid, newStatus }) => updateManifestStatus(uuid, newStatus),
    onSuccess: (data, variables) => {
      const statusLabel =
        variables.newStatus === "ASN" ? "Assigned" : "Completed";
      toast.success(`Manifest status updated to ${statusLabel}`);
      queryClient.invalidateQueries(["manifest", manifestUuid]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update manifest status");
    },
  });

  const handleSendToDriver = () => {
    setOpenDriverModal(true);
  };

  const handleCompleteManifest = () => {
    if (manifestUuid) {
      updateStatusMutation.mutate({ uuid: manifestUuid, newStatus: "COM" });
    }
  };

  const sendToDispatchersMutation = useMutation({
    mutationFn: ({ manifestUuid, dispatcherUuids }) =>
      sendManifestToDispatchers(manifestUuid, dispatcherUuids),
    onSuccess: () => {
      toast.success("Manifest sent to dispatchers successfully");
      setOpenDispatchersModal(false);
      queryClient.invalidateQueries(["manifest", manifestUuid]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send manifest to dispatchers");
    },
  });

  const handleOpenDispatchersModal = () => {
    setOpenDispatchersModal(true);
  };

  const handleCloseDispatchersModal = () => {
    setOpenDispatchersModal(false);
  };

  const handleCloseDriverModal = () => {
    setOpenDriverModal(false);
  };

  const handleSendToDriverAction = (options) => {
    if (options.method === "mark_only") {
      // Just mark as sent without actually sending
      toast.success("Manifest marked as sent");
      setOpenDriverModal(false);
      queryClient.invalidateQueries(["manifest", manifestUuid]);
    } else {
      // Actually send to driver
      if (manifestUuid && driverUuid) {
        sendToDriverMutation.mutate({ manifestUuid, driverUuid });
        setOpenDriverModal(false);
      } else {
        toast.error("Driver is required to send manifest");
      }
    }
  };

  const handleSendToDispatchers = (dispatcherUuids) => {
    if (manifestUuid && dispatcherUuids.length > 0) {
      sendToDispatchersMutation.mutate({ manifestUuid, dispatcherUuids });
    } else {
      toast.error("Please select at least one dispatcher");
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [invoiceMenuAnchorEl, setInvoiceMenuAnchorEl] = useState(null);
  const invoiceMenuOpen = Boolean(invoiceMenuAnchorEl);
  const [openDispatchersModal, setOpenDispatchersModal] = useState(false);
  const [openDriverModal, setOpenDriverModal] = useState(false);
  const [openAddOrdersModal, setOpenAddOrdersModal] = useState(false);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleInvoiceMenuClick = (event) => {
    setInvoiceMenuAnchorEl(event.currentTarget);
  };

  const handleInvoiceMenuClose = () => {
    setInvoiceMenuAnchorEl(null);
  };

  useEffect(() => {
    if (orderNumber) {
      document.title = orderNumber + " | " + status.toUpperCase();
    }
  }, [orderNumber]);

  const deleteManifestMutation = useMutation({
    mutationFn: (uuids) => deleteManifests(uuids),
    onSuccess: () => {
      toast.success("Manifest deleted successfully!");
      navigate("/manifests");
    },
    onError: (error) => {
      const message =
        error?.response?.data?.message || "Failed to delete manifest";
      toast.error(message);
    },
  });

  const handleDeleteManifest = (uuid) => {
    deleteManifestMutation.mutate([uuid]);
  };

  return (
    <AppBar
      position="relative"
      color="default"
      elevation={0}
      sx={{
        backgroundColor: "#f8f9fd",
        borderBottom: "1px solid #e0e0e0",
        p: 0,
      }}
    >
      {/* OSND Alert */}
      {isOSND && (
        <Alert
          severity="warning"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 0,
            fontSize: "1rem",
            "& .MuiAlert-icon": {
              fontSize: "1.5rem",
            },
            height: "40px",
            py: 0,
          }}
        >
          <Typography variant="h6">This is an OS&D order</Typography>
        </Alert>
      )}
      {/* <div className="w-full h-10 bg-white border-b border-gray-200"></div> */}
      <Toolbar
        sx={{ minHeight: "40px !important", px: "16px !important", pt: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <Breadcrumbs aria-label="breadcrumb" separator="/">
            <Link
              underline="hover"
              color="inherit"
              href="#"
              sx={{
                fontWeight: 500,
                color: "#1e55bf",
                textDecoration: "none",
              }}
            >
              {isManifest ? "Manifest" : "Orders"}
            </Link>
            <Typography
              color="text.primary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              {isManifest ? (
                <EditableInput
                  title=""
                  value={manifestName || orderNumber}
                  onSave={handleSaveName}
                  variant="body1"
                  isManifest={isManifest}
                />
              ) : (
                orderNumber
              )}
              <Chip
                label={getStatusLabel(status)}
                size="small"
                sx={{
                  height: "30px",
                  fontSize: "0.8rem",
                  backgroundColor: "#e3f2fd",
                  color: "#1e55bf",
                  fontWeight: 500,
                  borderRadius: "4px",
                  textTransform: "uppercase",
                }}
              />
            </Typography>
          </Breadcrumbs>
        </Box>
        {isManifest ? (
          <>
            {/* Send to Driver button - disabled only when manifest is MOV/COM, while updating, or when no driver */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenDispatchersModal}
              // disabled={
              //   status === "COM" ||
              //   status === "Completed" ||
              //   status === "MOV" ||
              //   status === "Moving"
              // }
              startIcon={<SendIcon />}
              sx={{
                mr: 1,
                textTransform: "none",
                borderRadius: "4px",
                backgroundColor: "#1e55bf",
              }}
            >
              Send to Dispatchers
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendToDriver}
              disabled={isDisabled}
              startIcon={
                sendToDriverMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{
                mr: 1,
                textTransform: "none",
                borderRadius: "4px",
                backgroundColor: "#1e55bf",
              }}
            >
              Send to Driver
            </Button>
            {/* Complete Manifest button - disabled if not assigned/moving or already completed */}
            <Button
              variant="contained"
              color="success"
              onClick={handleCompleteManifest}
              disabled={
                updateStatusMutation.isPending ||
                status === "COM" ||
                status === "Completed" ||
                (status !== "ASN" &&
                  status !== "Assigned" &&
                  status !== "MOV" &&
                  status !== "Moving")
              }
              startIcon={
                updateStatusMutation.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              sx={{
                mr: 1,
                textTransform: "none",
                borderRadius: "4px",
              }}
            >
              Complete Manifest
            </Button>
            <IconButton
              aria-label="more"
              aria-controls="manifest-menu"
              aria-haspopup="true"
              onClick={handleMenuClick}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="manifest-menu"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleMenuClose}
            >
              {/* <MenuItem onClick={handleMenuClose}>Back</MenuItem> */}
              <MenuItem
                onClick={() => handleDeleteManifest(manifestUuid)}
                disabled={deleteManifestMutation.isPending}
              >
                {deleteManifestMutation.isPending ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </MenuItem>
              {/* <MenuItem onClick={handleMenuClose}>Duplicate</MenuItem> */}
            </Menu>
          </>
        ) : !notSubmitButton ? (
          status?.toLowerCase() === "quoted" ? (
            <Button
              variant="contained"
              color="success"
              onClick={handleMarkAsDelivered}
              disabled={isMarkingDelivered}
              startIcon={
                isMarkingDelivered ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <LocalShippingIcon />
                )
              }
              sx={{
                mr: 1,
                textTransform: "none",
                borderRadius: "4px",
              }}
            >
              {isMarkingDelivered ? "Processing..." : "Mark as Delivered"}
            </Button>
          ) : status?.toLowerCase() === "delivered" && handleCreateInvoice ? (
            <>
              <ButtonGroup variant="contained" color="primary">
                <Button
                  onClick={() => handleCreateInvoice("single", orderId)}
                  disabled={isCreatingInvoice}
                  startIcon={
                    isCreatingInvoice ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <DescriptionIcon />
                    )
                  }
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    backgroundColor: "#1e55bf",
                  }}
                >
                  {isCreatingInvoice ? "Creating..." : "Create Invoice"}
                </Button>
                <Button
                  size="small"
                  onClick={handleInvoiceMenuClick}
                  disabled={isCreatingInvoice}
                  sx={{
                    backgroundColor: "#1e55bf",
                  }}
                >
                  <KeyboardArrowDown />
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={invoiceMenuAnchorEl}
                open={invoiceMenuOpen}
                onClose={handleInvoiceMenuClose}
              >
                <MenuItem
                  onClick={() => {
                    handleCreateInvoice("single", orderId);
                    handleInvoiceMenuClose();
                  }}
                  disabled={isCreatingInvoice}
                >
                  Single Invoice
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setOpenAddOrdersModal(true);
                    handleInvoiceMenuClose();
                  }}
                  disabled={isCreatingInvoice}
                >
                  Add to Existing Invoice
                </MenuItem>
              </Menu>
            </>
          ) : null
        ) : null}
        {status?.toLowerCase() === "new" ||
        status?.toLowerCase() === "saved" ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{
              mr: 1,
              textTransform: "none",
              borderRadius: "4px",
              backgroundColor: "#1e55bf",
            }}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin w-5 h-5 text-white" />
              </>
            ) : (
              <>Submit order</>
            )}
          </Button>
        ) : null}
        {isPending ? (
          <CircularProgress size={20} sx={{ color: "#1e55bf" }} />
        ) : null}
        {!isManifest && actionMenuItems && actionMenuItems?.length > 0 ? (
          <>
            <IconButton
              aria-label="more"
              aria-controls="long-menu"
              aria-haspopup="true"
              onClick={handleMenuClick}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleMenuClose}
              sx={{
                p:0
              }}
            >
              {actionMenuItems?.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    item.onClick();
                    handleMenuClose();
                  }}
                  sx={{
                    color: item.color || "inherit",
                    borderBottom: item.divider ? "1px solid #e0e0e0" : "none",
                    ...item.sx,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                  }}
                  disabled={item.disabled}
                >
                  {item.icon && (
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: item.iconColor || "inherit",
                      }}
                    >
                      {item.icon}
                    </Box>
                  )}
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        ) : null}
      </Toolbar>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          pb: 1,
          backgroundColor: "#f8f9fd",
          borderRadius: 0,
        }}
      >
        <Typography
          variant="body2"
          sx={{ display: "flex", alignItems: "center" }}
        >
          CSR:
          <Box sx={{ display: "flex", alignItems: "center", ml: 1 }}>
            <Avatar
              sx={{
                width: 24,
                height: 24,
                bgcolor: "#ffeb3b",
                color: "#000",
                fontSize: "0.75rem",
                mr: 1,
              }}
            >
              {userInfo.firstName && userInfo.lastName
                ? `${userInfo.firstName[0]}${userInfo.lastName[0]}`
                : ""}
            </Avatar>
            {userInfo.firstName} {userInfo.lastName}
          </Box>
        </Typography>
      </Box>
      <SendToDispatchersModal
        open={openDispatchersModal}
        handleClose={handleCloseDispatchersModal}
        onSend={handleSendToDispatchers}
        isLoading={sendToDispatchersMutation.isPending}
      />
      <SendToDriverModal
        open={openDriverModal}
        handleClose={handleCloseDriverModal}
        onSend={handleSendToDriverAction}
        isLoading={sendToDriverMutation.isPending}
        driver={driver}
        manifestUuid={manifestUuid}
      />
      <AddOrdersToInvoiceModal
        open={openAddOrdersModal}
        onClose={() => setOpenAddOrdersModal(false)}
        orderId={orderId}
      />
    </AppBar>
  );
};

export default TopBar;
